import { useEffect, useRef } from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import type { ISupportDiagnosticsDocument, ISupportDiagnosticsValidation } from '@srm/screen-sharing/light';
import type { IDocumentState } from '@/store/reducers/DocumentReducer';

export interface ISupportDocument {
    id: string;
    title?: string;
}

/** Сведения документа для пакета диагностики (§43.2) без навигатора «Заполнение»: его публикует рабочая область. */
export type SupportDocumentState = Omit<ISupportDiagnosticsDocument, 'id' | 'title' | 'completion'>;

/** Открытая карточка документа — для контекста поддержки при показе экрана. Модуль без зависимостей: его тянет карточка. */
let currentDocument: ISupportDocument | null = null;
let describeDocument: (() => SupportDocumentState | null) | null = null;
let describeCompletion: (() => ISupportDiagnosticsDocument['completion']) | null = null;

export const getSupportDocument = () => currentDocument;

/** @internal Для тестов. */
export const setSupportDocument = (document: ISupportDocument | null) => {
    currentDocument = document;
};

/** Карточка документа сообщает поддержке свой идентификатор и заголовок, пока смонтирована. */
export function useSupportDocumentContext(id: string | undefined, title: string | undefined): void {
    useEffect(() => {
        if (!id) return undefined;
        const entry = { id, title };
        currentDocument = entry;
        return () => {
            if (currentDocument === entry) currentDocument = null;
        };
    }, [id, title]);
}

/**
 * Карточка документа отдаёт пакету диагностики снимок своего состояния (режим, этап, несохранённое, ошибки проверки) —
 * функцией: он считается, только когда пользователь согласился отправить пакет.
 */
export function useSupportDocumentDiagnostics(describe: () => SupportDocumentState | null): void {
    const latest = useRef(describe);
    latest.current = describe;
    useEffect(() => {
        const entry = () => latest.current();
        describeDocument = entry;
        return () => {
            if (describeDocument === entry) describeDocument = null;
        };
    }, []);
}

/** Навигатор «Заполнение» (его состояние локально в рабочей области) публикует прогресс. Возвращает отписку. */
export function publishSupportCompletion(describe: () => ISupportDiagnosticsDocument['completion']): () => void {
    describeCompletion = describe;
    return () => {
        if (describeCompletion === describe) describeCompletion = null;
    };
}

/** Документ для пакета диагностики: открытая карточка, её состояние и прогресс «Заполнения»; без карточки — нет. */
export function supportDocumentDiagnostics(): ISupportDiagnosticsDocument | undefined {
    if (!currentDocument) return undefined;
    const state = describeDocument?.() ?? null;
    const completion = describeCompletion?.();
    return {
        id: currentDocument.id,
        ...(currentDocument.title ? { title: currentDocument.title } : {}),
        ...(state ?? { mode: 'view', dirty: false }),
        ...(completion ? { completion } : {}),
    };
}

const FIELD_PATH = /^fields\.(\d+)\.value(?:\.|$)/;

/** Путь формы → имя реквизита (`fields.3.value` → `amount`), как в «Ошибках формы» панели отладки. */
const fieldName = (path: string, names: readonly (string | undefined)[]) => {
    const match = FIELD_PATH.exec(path);
    return (match && names[Number(match[1])]) || path;
};

/** Листья дерева RHF (`dirtyFields`, `errors`) с путями. */
function leaves(node: unknown, isLeaf: (value: unknown) => boolean, prefix = '', out: [string, unknown][] = []) {
    if (!node || typeof node !== 'object') return out;
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (isLeaf(value)) out.push([path, value]);
        else leaves(value, isLeaf, path, out);
    }
    return out;
}

const isErrorLeaf = (value: unknown) => !!value && typeof value === 'object' && ('message' in value || 'type' in value);

/**
 * Состояние карточки для пакета диагностики: режим, «версия», этап, несохранённое (ключи полей, без значений) и ошибки
 * проверки RHF — по одному на поле.
 */
export function describeSupportDocument(
    doc: Pick<IDocumentState, 'isEdit' | 'isNew' | 'isHistoryView' | 'scheme' | 'data' | 'statusBar'>,
    form: { dirty: boolean; dirtyFields: unknown; errors: FieldErrors<FieldValues> },
): SupportDocumentState {
    const names = (doc.data?.fields ?? []).map((field) => field?.name);
    const modified = doc.data?.doc_ModifyDate as unknown;
    const dirtyFields = [
        ...new Set(leaves(form.dirtyFields, (value) => value === true).map(([path]) => fieldName(path, names))),
    ];
    const seen = new Set<string>();
    const validation: ISupportDiagnosticsValidation[] = [];
    for (const [path, value] of leaves(form.errors, isErrorLeaf)) {
        const label = fieldName(path, names);
        if (seen.has(label)) continue;
        seen.add(label);
        const error = value as { message?: unknown; type?: unknown };
        validation.push({
            key: path,
            label,
            message:
                typeof error.message === 'string' && error.message
                    ? error.message
                    : typeof error.type === 'string' && error.type
                      ? error.type
                      : 'Ошибка проверки',
        });
    }
    const stage = doc.statusBar?.levels?.map((level) => ({ title: level.title, status: level.status }));
    return {
        flowKey: doc.scheme?.flowKey || doc.data?.flowKey || undefined,
        formName: doc.scheme?.formName || undefined,
        modifiedAt:
            modified instanceof Date
                ? modified.toISOString()
                : typeof modified === 'string' && modified
                  ? modified
                  : undefined,
        hash: doc.data?.doc_Hash || undefined,
        ...(stage?.length ? { stage } : {}),
        mode: doc.isHistoryView ? 'history' : doc.isNew ? 'new' : doc.isEdit ? 'edit' : 'view',
        dirty: form.dirty,
        ...(dirtyFields.length ? { dirtyFields } : {}),
        ...(validation.length ? { validation } : {}),
    };
}
